// pages/LeaderPromptsSection.jsx — Anamoria SPA
// v1.2 — NEW-171 Phase 3 (PR-2): accept groupParam; thread ?group on the group-scoped prompt calls
//   (prompts list/add/reorder/status + prompt-mode) so a multi-cohort leader edits the SELECTED
//   cohort. The prompt LIBRARY (global template catalog) is not threaded. (Nov 1, 2026)
// v1.1 — May 25, 2026
// Track 2: Custom Group Prompts — leader prompt management component
//
// Changes from v1.0:
//   - Wrapped in parent card class for visual consistency
//   - Collapsible header (starts collapsed) with prompt count badge
//
// Self-contained section rendered inside LeaderPage.jsx.
// Manages its own state and API calls via the getApi prop.
//
// Features:
//   - Prompt mode toggle (Standard Prompts / Custom Prompts)
//   - Group prompt list with status badges
//   - Add prompt: select from library OR write custom
//   - Reorder prompts (move up/down)
//   - Deactivate/reactivate prompts
//
// Consumes:
//   GET   /leader/prompts          → group prompts + mode
//   POST  /leader/prompts          → add library or custom prompt
//   PATCH /leader/prompts/{id}     → reorder / deactivate / reactivate
//   GET   /leader/prompts/library  → available rotation prompts
//   PATCH /leader/prompt-mode      → toggle standard/custom

import { useState, useEffect, useCallback } from 'react';
import s from './LeaderPromptsSection.module.css';

export default function LeaderPromptsSection({ getApi, groupParam = '', cardClass, titleClass }) {
  // ── State ──
  const [promptMode, setPromptMode] = useState('standard');
  const [prompts, setPrompts] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [collapsed, setCollapsed] = useState(true);

  // ── Add prompt UI ──
  const [showAdd, setShowAdd] = useState(false);
  const [addTab, setAddTab] = useState('library'); // 'library' | 'custom'
  const [customText, setCustomText] = useState('');
  const [customTitle, setCustomTitle] = useState('');

  // ── Helpers ──
  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  }

  // ── Load prompts ──
  const loadPrompts = useCallback(async () => {
    try {
      const api = getApi();
      const data = await api.get(`/leader/prompts${groupParam}`);
      setPromptMode(data.promptMode || 'standard');
      setPrompts(data.prompts || []);
    } catch (err) {
      console.error('Failed to load prompts:', err);
    } finally {
      setLoading(false);
    }
  }, [getApi]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  // ── Load library (when add panel opens on library tab) ──
  async function loadLibrary() {
    if (library.length > 0) return; // already loaded
    setLibraryLoading(true);
    try {
      const api = getApi();
      const data = await api.get('/leader/prompts/library');
      setLibrary(data.prompts || []);
    } catch (err) {
      console.error('Failed to load library:', err);
      showToast('Failed to load prompt library');
    } finally {
      setLibraryLoading(false);
    }
  }

  // ── Toggle prompt mode ──
  async function handleModeToggle(newMode) {
    if (newMode === promptMode || saving) return;
    setSaving(true);
    try {
      const api = getApi();
      await api.patch(`/leader/prompt-mode${groupParam}`, { promptMode: newMode });
      setPromptMode(newMode);
    } catch (err) {
      showToast('Failed to update prompt mode');
    } finally {
      setSaving(false);
    }
  }

  // ── Add library prompt ──
  async function handleAddLibrary(promptId) {
    setSaving(true);
    try {
      const api = getApi();
      await api.post(`/leader/prompts${groupParam}`, { sourceType: 'library', promptId });
      // Remove from library list
      setLibrary(prev => prev.filter(p => p.id !== promptId));
      // Refresh prompts
      await loadPrompts();
      showToast('Prompt added to your group');
    } catch (err) {
      if (err.error === 'PROMPT_ALREADY_ADDED') {
        showToast('This prompt is already in your group');
      } else {
        showToast('Failed to add prompt');
      }
    } finally {
      setSaving(false);
    }
  }

  // ── Submit custom prompt ──
  async function handleSubmitCustom() {
    if (!customText.trim()) { showToast('Please enter prompt text'); return; }
    if (customText.length > 500) { showToast('Prompt text must be 500 characters or fewer'); return; }
    setSaving(true);
    try {
      const api = getApi();
      await api.post(`/leader/prompts${groupParam}`, {
        sourceType: 'custom',
        text: customText.trim(),
        title: customTitle.trim() || undefined,
      });
      setCustomText('');
      setCustomTitle('');
      setShowAdd(false);
      await loadPrompts();
      showToast('Custom prompt submitted for review');
    } catch (err) {
      if (err.error === 'MAX_PROMPTS_REACHED') {
        showToast('Maximum 25 prompts per group reached');
      } else {
        showToast('Failed to submit prompt');
      }
    } finally {
      setSaving(false);
    }
  }

  // ── Move prompt up/down ──
  async function handleMove(promptId, direction) {
    const approved = prompts.filter(p => p.status === 'approved');
    const idx = approved.findIndex(p => p.id === promptId);
    if (idx < 0) return;
    const newOrder = direction === 'up' ? Math.max(1, idx) : idx + 2; // 1-based
    setSaving(true);
    try {
      const api = getApi();
      await api.patch(`/leader/prompts/${promptId}${groupParam}`, { sortOrder: newOrder });
      await loadPrompts();
    } catch (err) {
      showToast('Failed to reorder');
    } finally {
      setSaving(false);
    }
  }

  // ── Deactivate / reactivate ──
  async function handleStatusChange(promptId, newStatus) {
    setSaving(true);
    try {
      const api = getApi();
      await api.patch(`/leader/prompts/${promptId}${groupParam}`, { status: newStatus });
      await loadPrompts();
      // If reactivating a library prompt, remove from library cache
      if (newStatus === 'approved') {
        setLibrary([]);  // force reload next time
      }
      showToast(newStatus === 'inactive' ? 'Prompt deactivated' : 'Prompt reactivated');
    } catch (err) {
      if (err.error === 'CUSTOM_REACTIVATION_NOT_ALLOWED') {
        showToast('Custom prompts must be resubmitted for approval');
      } else {
        showToast('Failed to update prompt');
      }
    } finally {
      setSaving(false);
    }
  }

  // ── Open add panel ──
  function openAdd() {
    setShowAdd(true);
    setAddTab('library');
    setCustomText('');
    setCustomTitle('');
    loadLibrary();
  }

  // ── Derived ──
  const approvedPrompts = prompts.filter(p => p.status === 'approved');
  const pendingPrompts = prompts.filter(p => p.status === 'pending');
  const rejectedPrompts = prompts.filter(p => p.status === 'rejected');
  const inactivePrompts = prompts.filter(p => p.status === 'inactive');

  // ── Render ──

  if (loading) {
    return (
      <div className={cardClass || s.section}>
        <div className={s.sectionLoading}>Loading prompts...</div>
      </div>
    );
  }

  const promptCount = prompts.filter(p => p.status === 'approved' || p.status === 'pending').length;

  return (
    <div className={cardClass || s.section}>
      <button className={s.collapseHeader} onClick={() => setCollapsed(!collapsed)}>
        <h3 className={titleClass || s.sectionTitle} style={{ margin: 0 }}>
          Group Prompts
          {promptCount > 0 && <span className={s.countBadge}>{promptCount}</span>}
        </h3>
        <span className={`${s.collapseArrow} ${collapsed ? '' : s.collapseArrowOpen}`}>▾</span>
      </button>

      {!collapsed && (
        <div className={s.collapseBody}>

      {/* ── Mode Toggle ── */}
      <div className={s.modeToggle}>
        <button
          className={`${s.modeBtn} ${promptMode === 'standard' ? s.modeBtnActive : ''}`}
          onClick={() => handleModeToggle('standard')}
          disabled={saving}
        >
          Standard Prompts
        </button>
        <button
          className={`${s.modeBtn} ${promptMode === 'custom' ? s.modeBtnActive : ''}`}
          onClick={() => handleModeToggle('custom')}
          disabled={saving}
        >
          Custom Prompts
        </button>
      </div>

      {promptMode === 'standard' && (
        <p className={s.modeHint}>
          Your group uses the standard prompt library. Participants see a rotating selection of prompts each time they visit.
        </p>
      )}

      {/* ── Custom Mode Content ── */}
      {promptMode === 'custom' && (
        <>
          {approvedPrompts.length === 0 && pendingPrompts.length === 0 && (
            <p className={s.modeHint}>
              Add prompts below to start using custom prompts for your group. You can select from the library or write your own.
            </p>
          )}

          {/* ── Approved Prompts ── */}
          {approvedPrompts.length > 0 && (
            <div className={s.promptGroup}>
              <div className={s.promptGroupHeader}>Active Prompts</div>
              {approvedPrompts.map((p, idx) => (
                <div key={p.id} className={s.promptCard}>
                  <div className={s.promptOrder}>{idx + 1}</div>
                  <div className={s.promptContent}>
                    <div className={s.promptTitle}>{p.title || 'Untitled'}</div>
                    <div className={s.promptText}>{p.text}</div>
                    <div className={s.promptMeta}>
                      <span className={`${s.badge} ${s.badgeApproved}`}>Active</span>
                      <span className={s.promptSource}>{p.sourceType === 'library' ? 'Library' : 'Custom'}</span>
                    </div>
                  </div>
                  <div className={s.promptActions}>
                    {idx > 0 && (
                      <button className={s.moveBtn} onClick={() => handleMove(p.id, 'up')} disabled={saving} title="Move up">↑</button>
                    )}
                    {idx < approvedPrompts.length - 1 && (
                      <button className={s.moveBtn} onClick={() => handleMove(p.id, 'down')} disabled={saving} title="Move down">↓</button>
                    )}
                    <button className={s.deactivateBtn} onClick={() => handleStatusChange(p.id, 'inactive')} disabled={saving}>
                      Deactivate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Pending Prompts ── */}
          {pendingPrompts.length > 0 && (
            <div className={s.promptGroup}>
              <div className={s.promptGroupHeader}>Awaiting Review</div>
              {pendingPrompts.map(p => (
                <div key={p.id} className={`${s.promptCard} ${s.promptCardPending}`}>
                  <div className={s.promptOrder}>—</div>
                  <div className={s.promptContent}>
                    <div className={s.promptTitle}>{p.title || 'Untitled'}</div>
                    <div className={s.promptText}>{p.text}</div>
                    <div className={s.promptMeta}>
                      <span className={`${s.badge} ${s.badgePending}`}>Pending</span>
                      <span className={s.promptSource}>Custom</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Rejected Prompts ── */}
          {rejectedPrompts.length > 0 && (
            <div className={s.promptGroup}>
              <div className={s.promptGroupHeader}>Not Approved</div>
              {rejectedPrompts.map(p => (
                <div key={p.id} className={`${s.promptCard} ${s.promptCardRejected}`}>
                  <div className={s.promptOrder}>—</div>
                  <div className={s.promptContent}>
                    <div className={s.promptTitle}>{p.title || 'Untitled'}</div>
                    <div className={s.promptText}>{p.text}</div>
                    <div className={s.promptMeta}>
                      <span className={`${s.badge} ${s.badgeRejected}`}>Not Approved</span>
                    </div>
                    {p.reviewNote && (
                      <div className={s.reviewNote}>Feedback: {p.reviewNote}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Inactive Prompts ── */}
          {inactivePrompts.length > 0 && (
            <div className={s.promptGroup}>
              <div className={s.promptGroupHeader}>Inactive</div>
              {inactivePrompts.map(p => (
                <div key={p.id} className={`${s.promptCard} ${s.promptCardInactive}`}>
                  <div className={s.promptOrder}>—</div>
                  <div className={s.promptContent}>
                    <div className={s.promptTitle}>{p.title || 'Untitled'}</div>
                    <div className={s.promptText}>{p.text}</div>
                    <div className={s.promptMeta}>
                      <span className={`${s.badge} ${s.badgeInactive}`}>Inactive</span>
                      <span className={s.promptSource}>{p.sourceType === 'library' ? 'Library' : 'Custom'}</span>
                    </div>
                  </div>
                  <div className={s.promptActions}>
                    {p.sourceType === 'library' && (
                      <button className={s.reactivateBtn} onClick={() => handleStatusChange(p.id, 'approved')} disabled={saving}>
                        Reactivate
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Add Prompt Button ── */}
          {!showAdd && (
            <button className={s.addBtn} onClick={openAdd} disabled={saving}>
              + Add Prompt
            </button>
          )}

          {/* ── Add Prompt Panel ── */}
          {showAdd && (
            <div className={s.addPanel}>
              <div className={s.addPanelHeader}>
                <h4 className={s.addPanelTitle}>Add Prompt</h4>
                <button className={s.closeBtn} onClick={() => setShowAdd(false)}>✕</button>
              </div>

              {/* Tabs */}
              <div className={s.addTabs}>
                <button
                  className={`${s.addTab} ${addTab === 'library' ? s.addTabActive : ''}`}
                  onClick={() => { setAddTab('library'); loadLibrary(); }}
                >
                  Select from Library
                </button>
                <button
                  className={`${s.addTab} ${addTab === 'custom' ? s.addTabActive : ''}`}
                  onClick={() => setAddTab('custom')}
                >
                  Write Custom
                </button>
              </div>

              {/* Library Tab */}
              {addTab === 'library' && (
                <div className={s.libraryList}>
                  {libraryLoading ? (
                    <div className={s.libraryLoading}>Loading library...</div>
                  ) : library.length === 0 ? (
                    <div className={s.libraryEmpty}>All available prompts have been added to your group.</div>
                  ) : (
                    library.map(p => (
                      <div key={p.id} className={s.libraryItem}>
                        <div className={s.libraryContent}>
                          <div className={s.libraryTitle}>{p.title}</div>
                          <div className={s.libraryText}>{p.text}</div>
                          <div className={s.libraryCategory}>{p.category}</div>
                        </div>
                        <button className={s.libraryAddBtn} onClick={() => handleAddLibrary(p.id)} disabled={saving}>
                          Add
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Custom Tab */}
              {addTab === 'custom' && (
                <div className={s.customForm}>
                  <div className={s.formField}>
                    <label className={s.formLabel}>Prompt Text *</label>
                    <textarea
                      className={s.formTextarea}
                      value={customText}
                      onChange={e => setCustomText(e.target.value)}
                      placeholder="e.g., What song reminds you most of {name}?"
                      rows={3}
                      maxLength={500}
                    />
                    <div className={s.charCount}>{customText.length}/500</div>
                  </div>
                  <div className={s.formField}>
                    <label className={s.formLabel}>Title (optional)</label>
                    <input
                      className={s.formInput}
                      type="text"
                      value={customTitle}
                      onChange={e => setCustomTitle(e.target.value)}
                      placeholder="e.g., A Song for Them"
                      maxLength={255}
                    />
                  </div>
                  <p className={s.customHint}>
                    Use {'{name}'} in your prompt text — it will be replaced with the person's name. Custom prompts require admin approval before they appear in your group's rotation.
                  </p>
                  <button className={s.submitBtn} onClick={handleSubmitCustom} disabled={saving || !customText.trim()}>
                    {saving ? 'Submitting...' : 'Submit for Review'}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* end collapse body */}
      </div>
      )}

      {/* Toast */}
      {toast && <div className={s.toast}>{toast}</div>}
    </div>
  );
}
